import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  Container,
  Modal,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router-dom";
import { useCheckout } from "./CheckoutContext";
import CloseIcon from "@mui/icons-material/Close";
import HoodieDescription from "./HoodieDescription";
import {
  IMG_SIZE_CHART_TSHIRT as SizeChartTshirt,
  IMG_BAUMWOLLE_ICON as BaumwolleIcon,
  IMG_ICON_SHIPPING as ShippingIcon,
  IMG_SPULE as SpuleIcon,
  IMG_SIZE_ICON as SizeIcon,
} from "./imageConfig";
import SizeChart from "./SizeChart";
import GotsIcon from "./images/GotsIcon";
import { useDrawer } from "./DrawerContext";
import emailjs from "emailjs-com";

const ProductDescription = ({ userCountry }) => {
  const { state } = useLocation();
  const { product, country } = state || {};
  const { dispatch } = useCheckout();

  const [size, setSize] = useState("S");
  const [
    quantity,
    //  setQuantity
  ] = useState(1);
  const [selectedImage, setSelectedImage] = useState(
    product?.images?.[0] || ""
  );
  const [openModal, setOpenModal] = useState(false);
  const [openSizeGuide, setOpenSizeGuide] = useState(false);
  const { openDrawer } = useDrawer();
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  const [openNotifyModal, setOpenNotifyModal] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(""); // can be success or error text
  const [isError, setIsError] = useState(false);
  const [selectedSize, setSelectedSize] = useState("S / Pink");
  const [selectedColor, setSelectedColor] = useState("BEIGE");
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!openNotifyModal) {
      // Reset states when modal closes
      setEmail("");
      setMessage("");
      setIsError(false);
    }
  }, [openNotifyModal]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Mobile
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet

  const handleNotifyClick = () => {
    if (!email.trim()) {
      setIsError(true);
      setMessage("Please specify an email address");
      return;
    }

    // Create template variables
    const templateParams = {
      user_email: email,
      selected_size: selectedSize, // from your size dropdown state
      current_date: new Date().toLocaleString(),
    };

    // Send email via EmailJS
    emailjs
      .send(
        "service_y0z3pso", // Your EmailJS service ID
        "template_zcugyuq", // Your EmailJS template ID
        templateParams, // Template variables
        "Feaz5xSkTHjhXg7Gi" // Your EmailJS public key
      )
      .then(
        () => {
          setIsError(false);
          setMessage("Your notification has been registered. Close");
          setTimeout(() => {
            setOpenNotifyModal(false);
          }, 3000);
        },
        (error) => {
          console.error("EmailJS Error:", error);
          setIsError(true);
          setMessage("Something went wrong. Please try again.");
        }
      );
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (product) {
      // Fire ViewContent pixel event
      window.fbq &&
        window.fbq("track", "ViewContent", {
          content_ids: [product.id],
          content_name: product.title,
          content_type: "product",
          value: product.prices[country?.code] || product.prices.default,
          currency: country?.currency || "USD",
        });
      setSelectedImage(product.images[0] || "");
      setSize("S");
    }
  }, [product, country]);

  const handleSizeChange = (event, newSize) => {
    if (newSize) setSize(newSize);
  };

  // const generateAmazonLink = (asin) => {
  //   return `https://www.amazon.${country.domain}/dp/${asin}`;
  // };

  const sizeRef = React.useRef(size);
  React.useEffect(() => {
    sizeRef.current = size; // Update ref when size changes
  }, [size]);

  const handleAddToCart = () => {
    setIsAddToCartLoading(true);

    setTimeout(() => {
      const cartItem = {
        id: `${product.id}-${sizeRef.current}`, // Unique ID = product.id + size
        productId: product.id, // Keep original ID for reference
        title: product.title,
        price: product.prices[country?.code] || product.prices.default,
        quantity: quantity,
        image: product.images[0],
        size: sizeRef.current,
        ...product,
      };

      dispatch({ type: "ADD_TO_CART", payload: cartItem });
      openDrawer();
      // Fire AddToCart pixel event
      window.fbq &&
        window.fbq("track", "AddToCart", {
          content_ids: [product.id],
          content_name: product.title,
          content_type: "product",
          value: cartItem.price,
          currency: country?.currency || "USD",
          quantity: quantity,
          size: sizeRef.current,
        });
      setIsAddToCartLoading(false);
    }, 1500);
  };

  // const handleAmazonBuy = () => {
  //   const link = generateAmazonLink(product.asin);
  //   window.open(link, "_blank", "noopener,noreferrer");
  // };

  const handleOpenModal = (img) => {
    // setModalImage(img);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  if (!product) {
    return (
      <Container>
        <Typography variant="h5" sx={{ mt: 4 }}>
          Product not found
        </Typography>
      </Container>
    );
  }

  // Extract product details from title (e.g., "DAINA T-Shirt | Beige | Logo Black")
  const productTitle = product?.title || "";
  const displayTitle = productTitle.replace(/\|/g, "");
  const titleParts = productTitle.split("|").map((p) => p.trim());
  const baseProductName = titleParts[0] || "DAINA T-Shirt";
  const productColor = titleParts[1]?.toLowerCase() || "white";
  const productLogoColor = titleParts[2]?.toLowerCase().replace("logo ", "") || "red";

  const features = [
    {
      icon: BaumwolleIcon,
      text: "ORGANIC\nCOTTON",
    },
    {
      icon: ShippingIcon,
      text: "CLIMATE\nNEUTRAL\nSHIPPING",
    },
    {
      icon: SpuleIcon,
      text: "MADE\nIN\nINDIA",
    },
    // {
    //   icon: UnisexIcon,
    //   text: "UNISEX",
    // },
    {
      icon: <GotsIcon />, // This can be an image or a React SVG
      text: "GOTS\nCERTIFIED",
    },
  ];

  const FeatureRow = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: isMobile ? 0 : 2,
          py: isMobile ? 2 : 4,
          flexWrap: isMobile ? "wrap" : "nowrap",
          maxWidth: "100%",
          gap: isMobile ? 1 : 2,
        }}
      >
        {features.map(({ icon, text }, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: isMobile ? "1 1 40%" : 1,
              minWidth: 0,
              mb: isMobile ? 1 : 0,
            }}
          >
            {/* Icon */}
            {typeof icon === "string" ? (
              <Box
                component="img"
                src={icon}
                alt=""
                sx={{
                  width: 35,
                  height: 35,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box>{icon}</Box>
            )}

            {/* Text */}
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? "10px" : "12px",
                whiteSpace: "pre-line",
                fontFamily: "inherit",
                fontWeight: 400,
                lineHeight: 1.2,
              }}
            >
              {text}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const sizeStock = {
    XS: product.isSoldOut ? 0 : 2,
    S: product.isSoldOut ? 0 : 2,
    M: product.isSoldOut ? 0 : 3,
    L: product.isSoldOut ? 0 : 3,
    XL: product.isSoldOut ? 0 : 2,
    XXL: product.isSoldOut ? 0 : 2,
  };

  return (
    <>
      <Box sx={{ maxWidth: "1600px", margin: "0 auto", pt: { xs: 12, md: 20 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 4, md: 8 } }}>

          {/* Left Column - Product Images Stacked */}
          <Box sx={{ flex: { xs: "1", md: "1.4" }, display: "flex", flexDirection: "column", gap: 2 }}>
            {product.images.map((img, index) => (
              <Box
                key={index}
                sx={{
                  width: "100%",
                  bgcolor: "#f9f9f9",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                <Box
                  component="img"
                  src={img}
                  alt={`${product.title} - ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "cover"
                  }}
                  onClick={() => handleOpenModal(img)}
                />

                {/* Save/Wishlist icon removed */}
              </Box>
            ))}
          </Box>

          {/* Right Column - Product Info Sticky */}
          <Box
            sx={{
              flex: "1",
              position: { xs: "static", md: "sticky" },
              top: 120,
              alignSelf: "flex-start",
              maxHeight: { md: "calc(100vh - 140px)" },
              overflowY: { md: "auto" },
              pr: { md: 2 },
              "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for info column
            }}
          >
            {/* Breadcrumbs (Mock) */}
            <Typography variant="caption" sx={{ color: "#999", mb: 2, display: "block", fontSize: "11px", letterSpacing: "0.5px" }}>
              HOME / {product.category?.toUpperCase()} / {displayTitle.toUpperCase()}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "22px", md: "28px" },
                  fontWeight: 400,
                  lineHeight: 1.2,
                  fontFamily: '"Playfair Display", serif',
                  color: "#000",
                  flex: 1
                }}
              >
                {displayTitle}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "18px",
                  fontWeight: 500,
                  ml: 2,
                  fontFamily: '"Inter", sans-serif'
                }}
              >
                {product.prices[country?.code] || product.prices.default}
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: "#f0f0f0" }} />

            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px", mb: 2, color: "#000" }}>
                COLOR — {selectedColor || "BEIGE"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: 0.5, py: 0.5, cursor: "pointer" }} onClick={() => setSelectedColor("BEIGE")}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#F9F4E8",
                    border: selectedColor === "BEIGE" ? "none" : "1.5px solid #bbb",
                    boxShadow: selectedColor === "BEIGE" ? "0 0 0 2px #fff, 0 0 0 3px #000" : "none",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: "#f0f0f0" }} />

            {/* SIZE Selection */}
            {(product.category === "hoodie" || product.category === "t-shirt") && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px" }}>
                      SIZE
                    </Typography>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#22c55e" }} />
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px" }}>IN STOCK</Typography>
                  </Box>
                  <Typography
                    onClick={() => setOpenSizeGuide(true)}
                    sx={{ fontSize: "11px", fontWeight: 500, textDecoration: "underline", cursor: "pointer", letterSpacing: "1px", color: "#666" }}
                  >
                    SIZE GUIDE
                  </Typography>
                </Box>

                <ToggleButtonGroup
                  value={product.isSoldOut ? "" : size}
                  exclusive
                  onChange={handleSizeChange}
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}
                >
                  {Object.keys(sizeStock).map((sz) => (
                    <ToggleButton
                      key={sz}
                      value={sz}
                      disabled={product.isSoldOut || sizeStock[sz] === 0}
                      sx={{
                        minWidth: "60px",
                        height: "48px",
                        border: "1px solid #ddd !important",
                        borderRadius: 0,
                        fontSize: "13px",
                        fontWeight: 400,
                        color: product.isSoldOut || sizeStock[sz] === 0 ? "#ccc" : "#000",
                        textTransform: "uppercase",
                        "&.Mui-selected": {
                          borderColor: "#000 !important",
                          borderWidth: "1px !important",
                          bgcolor: "transparent",
                          color: "#000",
                          fontWeight: 600
                        },
                        "&:hover": { bgcolor: "#f9f9f9" },
                        position: "relative",
                        "&::after": (product.isSoldOut || sizeStock[sz] === 0) ? {
                          content: '"✉"',
                          position: "absolute",
                          bottom: 2,
                          right: 2,
                          fontSize: "8px",
                          color: "#ccc"
                        } : {}
                      }}
                    >
                      {sz === "XS" ? "XS" : sz}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            <Typography variant="body2" sx={{ fontSize: "13px", color: "#333", mb: 4, fontFamily: '"Inter", sans-serif' }}>
              The model is 5'7 in. and wears size small
            </Typography>

            {(product.category === "hoodie" || product.category === "sweatshirt") && (
              <Typography sx={{ fontSize: "12px", letterSpacing: "1.5px", color: "#888", mb: 2, textTransform: "uppercase", textAlign: "center" }}>
                Coming in Winter
              </Typography>
            )}
            <Button
              variant="contained"
              fullWidth
              disableRipple
              disableElevation
              disabled={product.isSoldOut || product.category === "hoodie" || product.category === "sweatshirt" || isAddToCartLoading}
              onClick={handleAddToCart}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                borderRadius: 0,
                py: 2.2,
                fontSize: "13px",
                fontWeight: 600,
                letterSpacing: "2.5px",
                textTransform: "uppercase",
                mb: 6,
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: "#1c1c1c", transform: "translateY(-1px)" },
                "&.Mui-disabled": { bgcolor: "#f0f0f0", color: "#aaa" }
              }}
            >
              {isAddToCartLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (product.isSoldOut ? "SOLD OUT" : (product.category === "hoodie" || product.category === "sweatshirt") ? "COMING IN WINTER" : "ADD TO BAG")}
            </Button>

            <Box sx={{ mb: 6 }}>
              <Typography variant="body2" sx={{ fontSize: "14px", lineHeight: 1.8, color: "#000", mb: isExpanded ? 3 : 1, fontFamily: '"Inter", sans-serif' }}>
                {titleParts.length >= 3
                  ? `${baseProductName} in ${productColor} with logo print in ${productLogoColor} made from 100% organic cotton.`
                  : `${productTitle} made from 100% organic cotton.`
                }
              </Typography>

              <Collapse in={isExpanded}>
                <Typography variant="body2" sx={{ fontSize: "14px", lineHeight: 1.8, color: "#000", mb: 3, fontFamily: '"Inter", sans-serif' }}>
                  The {baseProductName}, made from high-quality cotton, is ethically produced in India.
                </Typography>
                <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none", mb: 3 }}>
                  {["Loose fit", "High-quality logo print", "GOTS certified"].map((item) => (
                    <Typography key={item} component="li" sx={{ fontSize: "14px", lineHeight: 1.8, color: "#000", "&::before": { content: '"- "', mr: 1 } }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ fontSize: "14px", lineHeight: 1.8, color: "#000", mb: 3, fontFamily: '"Inter", sans-serif' }}>
                  For a figure-hugging fit, we recommend choosing one size smaller. Discover the {baseProductName}s in other colors.
                </Typography>
              </Collapse>

              <Typography
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ fontSize: "14px", fontWeight: 500, textDecoration: "underline", cursor: "pointer", mt: 1 }}
              >
                {isExpanded ? "Read less" : "Read more"}
              </Typography>
            </Box>

            {/* Accordions */}
            <Box sx={{ borderTop: "0.5px solid #ddd" }}>
              <Accordion disableGutters sx={{ boxShadow: "none", m: "0 !important", "&:before": { display: "none" }, borderBottom: "0.5px solid #ddd", borderRadius: "0 !important" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: "22px", color: "#000" }} />}
                  sx={{ px: 0, py: 1 }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px", color: "#000" }}>MATERIAL</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pb: 3 }}>
                  <Typography sx={{ fontSize: "14px", color: "#000", lineHeight: 1.8, mb: 1 }}>100% Organic Cotton</Typography>
                  <Typography sx={{ fontSize: "14px", color: "#000", lineHeight: 1.8, mb: 1 }}>Made in India</Typography>
                  <Typography sx={{ fontSize: "14px", color: "#000", lineHeight: 1.8 }}>GOTS Certified</Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters sx={{ boxShadow: "none", m: "0 !important", "&:before": { display: "none" }, borderBottom: "0.5px solid #ddd", borderRadius: "0 !important" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: "22px", color: "#000" }} />}
                  sx={{ px: 0, py: 1 }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px", color: "#000" }}>CARE INSTRUCTIONS</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pb: 3 }}>
                  <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none" }}>
                    {[
                      "Machine wash up to 30°C",
                      "Wash inside out",
                      "Do not bleach",
                      "Do not dry clean",
                      "Do not tumble dry",
                      "Iron inside out at low temperature",
                      "Do not iron embroidery or prints with high heat.",
                      "Since cotton is a natural fiber, product may shrink up to 5% during the first wash"
                    ].map((item) => (
                      <Typography
                        key={item}
                        component="li"
                        sx={{
                          fontSize: "14px",
                          lineHeight: 1.8,
                          color: "#000",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          mb: 1.5,
                          "&::before": {
                            content: '"•"',
                            color: "#000",
                            fontSize: "18px",
                            lineHeight: "1.2"
                          }
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            {/* Sustainability Icons */}
            <Box sx={{ mt: 6 }}>
              <FeatureRow />
            </Box>
          </Box>
        </Box>

        <Modal
          open={openSizeGuide}
          onClose={() => setOpenSizeGuide(false)}
          sx={{ backdropFilter: "blur(4px)" }}
        >
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: isSmallScreen ? "84%" : isTablet ? "85%" : "70%",
              maxWidth: 900,
              bgcolor: "background.paper",
              borderRadius: 4,
              boxShadow: 24,
              p: isSmallScreen ? 1.5 : 3,
              maxHeight: "85vh",
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => setOpenSizeGuide(false)}
              sx={{
                position: "absolute",
                top: isSmallScreen ? 12 : 16,
                right: isSmallScreen ? 12 : 16,
                zIndex: 10,
                backgroundColor: "#333",
                color: "#DEDEDE",
                width: isSmallScreen ? 30 : 35,
                height: isSmallScreen ? 30 : 35,
                borderRadius: "50%",
                "&:hover": {
                  backgroundColor: "#333",
                  "& svg": { color: "white" },
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Image */}
            <Box sx={{ textAlign: "center" }}>
              <img
                src={SizeChartTshirt}
                alt="Size Chart"
                style={{
                  maxWidth: isSmallScreen ? "100%" : "60%",
                  height: "auto",
                  borderRadius: 8,
                }}
              />
            </Box>
            <SizeChart />
          </Box>
        </Modal>

        {/* Modal for Image Gallery */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          sx={{
            backdropFilter: "blur(4px)",
          }}
        >
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              // Remove backdrop styles from here as they're now in Modal
            }}
            onClick={handleCloseModal} // Close when clicking on backdrop
          >
            <Box
              sx={{
                position: "relative",
                width: "80vw",
                height: "90vh",
                bgcolor: "background.paper",
                boxShadow: 24,
                overflowY: "scroll",
                borderRadius: 4,
                p: 2,
              }}
              onClick={(e) => e.stopPropagation()} // Prevent click inside from closing
            >
              {/* Close Button (unchanged) */}
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 10,
                  backgroundColor: "#333",
                  color: "rgb(222.72, 222.72, 227.72)",
                  width: 35,
                  height: 35,
                  borderRadius: "50%",
                  "& .MuiSvgIcon-root": {
                    fontSize: "20px",
                    fontWeight: "bold",
                  },
                  "&:hover": {
                    backgroundColor: "#333",
                    "& svg": {
                      color: "white",
                    },
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* All Images */}
              {product.images.map((img, index) => (
                <Box key={index} sx={{ mb: 2, textAlign: "center" }}>
                  <img
                    src={img}
                    alt={`Zoom ${index}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "80vh",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default ProductDescription;
